import React, { useCallback, useMemo, useState } from 'react';
import { useGasI18n, useGasNotify, useGasConfirm } from '../../shared/kit';
import { formatPrice } from '../shop-shared/format';
import { addToCart as apiAddToCart, emptyCart as apiEmptyCart, errorExtras } from './api';
import PhysicalBuybox from './PhysicalBuybox';
import type { ProductVariante, ShopProductInitData } from './types';

interface AppProps {
    initData: ShopProductInitData;
    /** Mount DOM de cette instance (multi buy-box). */
    mountEl?: HTMLElement | null;
}

interface ScreenMessage {
    id: number;
    type: 'success' | 'error' | 'info';
    text: string;
}

interface GiftState {
    isGift: boolean;
    email: string;
    message: string;
}

/** Signature i18n du kit : t(clé, params?, défaut?). */
type TFn = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

const NAME_YOUR_PRICE_STEP = 0.05;

/** true si le produit est en paiement conscient (le client choisit son montant). */
function isNameYourPriceProduct(cfg: ShopProductInitData): boolean {
    return cfg.price_mode === 'name_your_price';
}

/**
 * Bornes Name Your Price résolues côté client (purement indicatives — le serveur re-borne).
 * `min` ne descend jamais sous 0 ; `suggested` est ramené dans [min, max] s'il existe.
 */
function resolveNameYourPrice(cfg: ShopProductInitData): { min: number; max: number | null; suggested: number } {
    const min = Math.max(0, Number(cfg.name_your_price_min ?? 0) || 0);
    const max = cfg.name_your_price_max !== null && cfg.name_your_price_max !== undefined ? Math.max(min, Number(cfg.name_your_price_max)) : null;
    let suggested = Number(cfg.name_your_price_suggested ?? 0) || 0;
    if (suggested < min) {
        suggested = min;
    }
    if (max !== null && suggested > max) {
        suggested = max;
    }
    return { min, max, suggested };
}

/** Présélections de montant : suggested, ×2, ×5, bornées au plafond produit s'il existe. */
function buildNameYourPricePresets(suggested: number, max: number | null): number[] {
    const base = suggested > 0 ? suggested : 10;
    const raw = [base, base * 2, base * 5];
    const filtered = raw.filter((v) => max === null || v <= max);
    // Dédoublonner (×2 = base si base très petit, etc.) en conservant l'ordre.
    return Array.from(new Set(filtered.map((v) => Math.round(v * 100) / 100)));
}

export default function App({ initData, mountEl = null }: AppProps) {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const { confirm } = useGasConfirm();

    const cfg = initData;
    const isInvitationMode = Boolean(cfg.invitation && cfg.invitation.valid);

    const [variantes, setVariantes] = useState<ProductVariante[]>(
        Array.isArray(cfg.variantes) ? cfg.variantes : [],
    );
    const [quantities, setQuantities] = useState<Record<string, number>>(() => {
        const init: Record<string, number> = {};
        (Array.isArray(cfg.variantes) ? cfg.variantes : []).forEach((v) => {
            init[String(v.variante_id)] = 1;
        });
        return init;
    });
    const [addingToCart, setAddingToCart] = useState<Record<string, boolean>>({});
    const [messages, setMessages] = useState<ScreenMessage[]>([]);
    const [gift, setGift] = useState<GiftState>({ isGift: false, email: '', message: '' });
    const msgCounter = React.useRef(0);

    const isNameYourPrice = isNameYourPriceProduct(cfg);
    // Montant Name Your Price choisi par variante (chaîne pour piloter un <input> contrôlé).
    // Pré-rempli avec le montant suggéré.
    const [nameYourPriceAmounts, setNameYourPriceAmounts] = useState<Record<string, string>>(() => {
        if (!isNameYourPrice) {
            return {};
        }
        const { suggested } = resolveNameYourPrice(cfg);
        const init: Record<string, string> = {};
        (Array.isArray(cfg.variantes) ? cfg.variantes : []).forEach((v) => {
            init[String(v.variante_id)] = suggested > 0 ? String(suggested) : '';
        });
        return init;
    });

    const globalPlacesInfo = useMemo(() => {
        const first = variantes[0];
        return first && first.places_info ? first.places_info : null;
    }, [variantes]);

    const cartTotalQuantity = useMemo(() => {
        const items = cfg.cart_status?.cart_items;
        if (!Array.isArray(items)) {
            return 0;
        }
        return items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
    }, [cfg.cart_status]);

    const addMessage = useCallback((type: ScreenMessage['type'], text: string) => {
        msgCounter.current += 1;
        const id = msgCounter.current;
        setMessages((prev) => [...prev, { id, type, text }]);
        window.setTimeout(() => {
            setMessages((prev) => prev.filter((m) => m.id !== id));
        }, 8000);
    }, []);

    const dismissMessage = useCallback((id: number) => {
        setMessages((prev) => prev.filter((m) => m.id !== id));
    }, []);

    const scrollToTop = useCallback(() => {
        const el = mountEl ?? document.getElementById('gas-shop-product-app');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [mountEl]);

    const updateGlobalCartInfo = useCallback((data: { cart_count?: number; cart_total?: number }) => {
        if (typeof data.cart_count === 'number') {
            document.querySelectorAll('.gas-shop-cart-count').forEach((node) => {
                node.textContent = String(data.cart_count);
            });
        }
        if (typeof data.cart_total !== 'undefined') {
            document.querySelectorAll('.gas-shop-cart-total').forEach((node) => {
                node.textContent = formatPrice(data.cart_total as number);
            });
        }
        document.dispatchEvent(new CustomEvent('gas_cart_updated', { detail: data }));
    }, []);

    const mutateVariante = useCallback((varianteId: number | null, patch: Partial<ProductVariante>) => {
        setVariantes((prev) => prev.map((v) => (v.variante_id === varianteId ? { ...v, ...patch } : v)));
    }, []);

    const doAddToCart = useCallback(async (v: ProductVariante) => {
        const key = String(v.variante_id);
        if (cfg.show_gift_section && gift.isGift && !gift.email.trim()) {
            notify.error(t('shop.product.gift_email_required'));
            addMessage('error', t('shop.product.gift_email_required'));
            return;
        }
        setAddingToCart((prev) => ({ ...prev, [key]: true }));
        try {
            const qty = Number(quantities[key] || 1);
            const payload: {
                product_id: number;
                product_type: string;
                variante_id: number | string;
                quantity: number;
                invitation_token?: string;
                gift_recipient_email?: string;
                gift_message?: string;
                name_your_price_amount?: number;
                shop_product_id?: number | null;
                shop_variant_id?: number;
            } = {
                product_id: cfg.product_id,
                shop_product_id: cfg.shop_product_id ?? undefined,
                product_type: cfg.product_type,
                variante_id: v.variante_id ?? 'default',
                quantity: qty,
            };
            // Mono-SKU physique : propage la variante implicite si connue (M2/M6).
            const implicitId = cfg.physical_buybox?.implicit_variant_id;
            if (implicitId && implicitId > 0 && cfg.product_type === 'produit_physique') {
                payload.shop_variant_id = implicitId;
            }
            if (v.form?.invitation_token) {
                payload.invitation_token = v.form.invitation_token;
            }
            // Paiement conscient : envoyer le montant choisi. Le serveur le re-borne
            // dans [min, plafond] (GasNameYourPriceHandler) — pas besoin de sécuriser ici.
            if (isNameYourPrice) {
                const raw = Number(nameYourPriceAmounts[key]);
                if (Number.isFinite(raw) && raw >= 0) {
                    payload.name_your_price_amount = raw;
                }
            }
            if (cfg.show_gift_section && gift.isGift) {
                payload.gift_recipient_email = gift.email;
                if (gift.message) {
                    payload.gift_message = gift.message;
                }
            }

            const data = await apiAddToCart(cfg, payload);

            if (data.cart_was_cleared && data.info_message) {
                addMessage('info', data.info_message);
            }
            addMessage('success', data.message || t('shop.cart.added_success'));
            mutateVariante(v.variante_id, {
                in_cart: true,
                cart_quantity: (v.cart_quantity || 0) + qty,
                show_form: false,
            });
            setGift({ isGift: false, email: '', message: '' });
            updateGlobalCartInfo(data);
            scrollToTop();
        } catch (error) {
            const extras = errorExtras(error);
            if (extras.require_login) {
                window.location.href = cfg.login_url;
                return;
            }
            if (extras.require_cart_action) {
                const ok = await confirm({
                    title: t('shop.cart.action_required_title'),
                    message: t('shop.cart.action_required_empty_and_add'),
                    confirmLabel: t('shop.cart.empty_and_add'),
                    tone: 'warning',
                });
                if (ok) {
                    try {
                        await apiEmptyCart(cfg);
                        await doAddToCart(v);
                    } catch (e) {
                        addMessage('error', (e as Error).message || t('errors.generic'));
                    }
                }
                return;
            }
            notify.error((error as Error).message || t('errors.generic'));
            addMessage('error', (error as Error).message || t('errors.generic'));
        } finally {
            setAddingToCart((prev) => ({ ...prev, [key]: false }));
        }
    }, [cfg, quantities, gift, isNameYourPrice, nameYourPriceAmounts, addMessage, mutateVariante, updateGlobalCartInfo, scrollToTop, confirm, notify, t]);

    // ---- Rendu ----

    if (cfg.invitation && !cfg.invitation.valid) {
        return (
            <div className="gas-shop-product-form" data-product-id={cfg.product_id} data-product-type={cfg.product_type}>
                <div className="gas-shop-invitation-error gas-shop-message gas-shop-error">
                    <h3>❌ {t('shop.invitation.invalid_title')}</h3>
                    <p>{cfg.invitation.error}</p>
                </div>
            </div>
        );
    }

    // M6 — buy-box multi-axes physiques (fallback mono-SKU / activité = UI historique).
    const physicalBuybox = cfg.physical_buybox;
    if (physicalBuybox?.has_axes && Array.isArray(physicalBuybox.axes) && physicalBuybox.axes.length > 0) {
        return (
            <div className="gas-shop-product-form" data-product-id={cfg.product_id} data-product-type={cfg.product_type}>
                <PhysicalBuybox initData={cfg} buybox={physicalBuybox} mountEl={mountEl} />
            </div>
        );
    }

    return (
        <div className="gas-shop-product-form" data-product-id={cfg.product_id} data-product-type={cfg.product_type}>
            {isInvitationMode && cfg.invitation && (
                <div className="gas-shop-invitation-banner">
                    <div className="gas-invitation-icon">🎁</div>
                    <div className="gas-invitation-content">
                        <h3>{t('shop.invitation.free_title')}</h3>
                        <p>
                            {t('shop.invitation.offered_by').split('%name%')[0]}
                            <strong>{cfg.invitation.member_name || ''}</strong>
                            {t('shop.invitation.offered_by').split('%name%').slice(1).join('%name%')}
                        </p>
                        <p className="gas-invitation-note">{t('shop.invitation.choose_formula')}</p>
                    </div>
                </div>
            )}

            {!isInvitationMode && cfg.cart_status && (
                cfg.cart_status.needs_cleanup ? (
                    <div className="gas-shop-cart-cleanup-message gas-shop-message gas-shop-info">
                        <p><strong>{t('shop.cart.cleanup_info')}</strong></p>
                        <ul>
                            {cfg.cart_status.cleanup_reasons.map((reason) => (
                                <li key={reason}>{reason}</li>
                            ))}
                        </ul>
                    </div>
                ) : cfg.cart_status.in_cart ? (
                    <div className="gas-shop-already-in-cart-message gas-shop-message gas-shop-success">
                        <p><strong>✓ {t('shop.cart.already_in_cart')}</strong></p>
                        {cartTotalQuantity > 1 ? (
                            <p>{t('shop.cart.already_in_cart_qty', { count: cartTotalQuantity })}</p>
                        ) : (
                            <p>{t('shop.cart.already_in_cart_single')}</p>
                        )}
                        <div className="gas-shop-cart-actions">
                            <a href={cfg.cart_url} className="gas-shop-action-button">{t('shop.cart.view_cart')}</a>
                        </div>
                    </div>
                ) : null
            )}

            {!isInvitationMode && cfg.access_info_html && (
                <div dangerouslySetInnerHTML={{ __html: cfg.access_info_html }} />
            )}

            {!isInvitationMode && cfg.global_free_tickets_notice && (
                <div className="gas-shop-global-free-tickets-notice">
                    <span className="gas-notice-icon">⚠️</span>
                    <span className="gas-notice-text" dangerouslySetInnerHTML={{
                        __html: t('shop.product.free_tickets_used_notice', {
                            count: cfg.global_free_tickets_notice.tickets_used,
                            price: formatPrice(cfg.global_free_tickets_notice.standard_price),
                        }),
                    }} />
                </div>
            )}

            <div className="gas-shop-product-messages">
                {messages.map((msg) => (
                    <div key={msg.id} className={`gas-shop-message gas-shop-${msg.type}`}>
                        <span>{msg.text}</span>
                        {msg.type === 'success' && (
                            <div className="gas-shop-cart-actions">
                                <a href={cfg.cart_url} className="gas-shop-action-button">{t('shop.cart.view_cart')}</a>
                                <button type="button" className="gas-shop-continue-shopping" onClick={() => dismissMessage(msg.id)}>
                                    {t('shop.cart.continue_shopping')}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {variantes.length === 0 ? (
                <div className="gas-shop-no-variantes">
                    {isInvitationMode ? t('shop.product.no_formula_invitation') : t('shop.product.no_variante')}
                </div>
            ) : (
                <div className={variantes.length > 1 ? 'gas-shop-variantes-prices' : undefined}>
                    {variantes.length > 1 && <h3>{t('shop.product.choose_formula')}</h3>}

                    {variantes.length > 1 && globalPlacesInfo && globalPlacesInfo.show && (
                        <div className="places-info-block places-info-global">
                            {globalPlacesInfo.remaining <= 0 ? (
                                <span className="places-badge places-badge--full">❌ {t('shop.product.activity_full')}</span>
                            ) : globalPlacesInfo.percentage >= 80 ? (
                                <span className="places-badge places-badge--urgent">⚠️ {t('shop.product.places_left', { count: globalPlacesInfo.remaining })}</span>
                            ) : (
                                <span className="places-badge places-badge--available">{t('shop.product.places_remaining', { remaining: globalPlacesInfo.remaining, max: globalPlacesInfo.max ?? 0 })}</span>
                            )}
                        </div>
                    )}

                    {variantes.map((v) => (
                        <VarianteBlock
                            key={String(v.variante_id)}
                            v={v}
                            cfg={cfg}
                            quantity={quantities[String(v.variante_id)] || 1}
                            adding={Boolean(addingToCart[String(v.variante_id)])}
                            gift={gift}
                            isNameYourPrice={isNameYourPrice}
                            nameYourPriceAmount={nameYourPriceAmounts[String(v.variante_id)] ?? ''}
                            onNameYourPriceAmountChange={(value) => setNameYourPriceAmounts((prev) => ({ ...prev, [String(v.variante_id)]: value }))}
                            onGiftChange={setGift}
                            onQuantityChange={(qty) => setQuantities((prev) => ({ ...prev, [String(v.variante_id)]: qty }))}
                            onAdd={() => doAddToCart(v)}
                            t={t}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface VarianteBlockProps {
    v: ProductVariante;
    cfg: ShopProductInitData;
    quantity: number;
    adding: boolean;
    gift: GiftState;
    isNameYourPrice: boolean;
    nameYourPriceAmount: string;
    onNameYourPriceAmountChange: (value: string) => void;
    onGiftChange: (gift: GiftState) => void;
    onQuantityChange: (qty: number) => void;
    onAdd: () => void;
    t: TFn;
}

function VarianteStock({ v, t }: { v: ProductVariante; t: VarianteBlockProps['t'] }) {
    if (v.exclude_calcul_places) {
        if (v.places_max !== null && v.places_max > 0 && v.stock !== null) {
            if (v.stock <= 0) return <span className="gas-shop-places-full">❌ {t('shop.product.stock_full')}</span>;
            if (v.stock <= 3) return <span className="gas-shop-places-warning">⚠️ {t('shop.product.stock_warning', { n: v.stock })}</span>;
            return <span className="gas-shop-stock-info">{t('shop.product.stock_info', { n: v.stock })}</span>;
        }
        return null;
    }
    if (v.places_max !== null && v.places_max > 0 && v.stock !== null) {
        if (v.stock <= 0) return <span className="gas-shop-places-full">❌ {t('shop.product.places_full')}</span>;
        if (v.stock <= 3) return <span className="gas-shop-places-warning">⚠️ {t('shop.product.places_warning', { n: v.stock })}</span>;
        return <span className="gas-shop-stock-info">{t('shop.product.places_info', { n: v.stock })}</span>;
    }
    return null;
}

function VariantePurchaseBadge({ v, t }: { v: ProductVariante; t: VarianteBlockProps['t'] }) {
    const pi = v.purchase_info;
    if (pi.already_purchased) {
        if (pi.allows_multiple) {
            return pi.limit_reached ? (
                <div className="gas-shop-product-purchase-badge">
                    <div className="gas-shop-purchase-info gas-shop-purchase-warning">
                        <span className="gas-purchase-icon">⚠️</span>
                        <span className="gas-purchase-text">{t('shop.product.purchased_limit', { purchased: pi.purchased_count, max: pi.max_allowed })}</span>
                    </div>
                </div>
            ) : (
                <div className="gas-shop-product-purchase-badge">
                    <div className="gas-shop-purchase-info gas-shop-purchase-success">
                        <span className="gas-purchase-icon">✓</span>
                        <span className="gas-purchase-text">{t('shop.product.purchased_remaining', { purchased: pi.purchased_count, remaining: pi.remaining })}</span>
                    </div>
                </div>
            );
        }
        return (
            <div className="gas-shop-product-purchase-badge">
                <div className="gas-shop-purchase-info gas-shop-purchase-success">
                    <span className="gas-purchase-icon">✓</span>
                    <span className="gas-purchase-text"><strong>{t('shop.product.purchased_single')}</strong></span>
                </div>
            </div>
        );
    }
    if (pi.show_login_prompt) {
        return (
            <div className="gas-shop-product-purchase-badge">
                <div className="gas-shop-purchase-info gas-shop-purchase-login">
                    <span className="gas-purchase-icon">🔐</span>
                    <span className="gas-purchase-text">{t('shop.product.login_to_check')}</span>
                </div>
            </div>
        );
    }
    return null;
}

function VarianteQuantityLimits({ v, t }: { v: ProductVariante; t: VarianteBlockProps['t'] }) {
    const f = v.form;
    if (!f || !f.show_quantity_selector) return null;
    if (f.max_effective === f.max_remaining && f.max_remaining < f.max_per_person) {
        return <small>{t('shop.product.qty_remaining', { remaining: f.max_remaining, max: f.max_per_person })}</small>;
    }
    if (f.max_effective === f.max_per_commande && f.max_per_commande > 1) {
        return f.has_variant_limit
            ? <small>{t('shop.product.qty_max_variant', { max: f.max_per_commande })}</small>
            : <small>{t('shop.product.qty_max_order', { max: f.max_per_commande })}</small>;
    }
    return null;
}

function NameYourPriceChooser({ cfg, amount, onAmountChange, varianteId, t }: {
    cfg: ShopProductInitData;
    amount: string;
    onAmountChange: (value: string) => void;
    varianteId: number | null;
    t: TFn;
}) {
    const { min, max, suggested } = resolveNameYourPrice(cfg);
    const presets = buildNameYourPricePresets(suggested, max);
    const currentNum = Number(amount);
    const inputId = `name_your_price_amount_${varianteId ?? 'default'}`;

    return (
        <div className="gas-shop-name-your-price">
            <p className="gas-shop-name-your-price__title">{t('shop.name_your_price.choose_amount', {}, 'Choisissez votre montant')}</p>
            <p className="gas-shop-name-your-price__hint">
                {min > 0
                    ? t('shop.name_your_price.hint_min', { min: min.toFixed(2) }, 'Montant libre, minimum %min% CHF.')
                    : t('shop.name_your_price.hint_free', {}, 'Payez le montant de votre choix.')}
            </p>

            {presets.length > 0 && (
                <div className="gas-shop-name-your-price__presets" role="group" aria-label={t('shop.name_your_price.presets_label', {}, 'Montants suggérés')}>
                    {presets.map((preset) => {
                        const active = Math.abs(currentNum - preset) < 0.005;
                        return (
                            <button
                                key={preset}
                                type="button"
                                className={`gas-shop-name-your-price__preset${active ? ' gas-shop-name-your-price__preset--active' : ''}`}
                                aria-pressed={active}
                                onClick={() => onAmountChange(String(preset))}
                            >
                                {t('shop.price.amount_chf', { amount: preset.toFixed(2) }, '%amount% CHF')}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="gas-shop-name-your-price__custom">
                <label htmlFor={inputId} className="gas-shop-name-your-price__label">
                    {t('shop.name_your_price.custom_label', {}, 'Autre montant (CHF)')}
                </label>
                <div className="gas-shop-name-your-price__input-wrap">
                    <input
                        id={inputId}
                        type="number"
                        className="gas-shop-name-your-price__input"
                        inputMode="decimal"
                        min={min}
                        {...(max !== null ? { max } : {})}
                        step={NAME_YOUR_PRICE_STEP}
                        value={amount}
                        placeholder={suggested > 0 ? suggested.toFixed(2) : '0.00'}
                        onChange={(e) => onAmountChange(e.target.value)}
                    />
                    <span className="gas-shop-name-your-price__currency">{t('shop.name_your_price.currency', {}, 'CHF')}</span>
                </div>
            </div>
        </div>
    );
}

function VarianteBlock({ v, cfg, quantity, adding, gift, isNameYourPrice, nameYourPriceAmount, onNameYourPriceAmountChange, onGiftChange, onQuantityChange, onAdd, t }: VarianteBlockProps) {
    const hasRestriction = v.restrictions.indexOf('membres') !== -1 || v.restrictions.indexOf('pass') !== -1;
    const blocked = v.member_blocked || v.pass_blocked;

    return (
        <div className={`gas-shop-variante-block${adding ? ' gas-shop-variante-block--loading' : ''}`} data-variante-id={v.variante_id ?? 'default'}>
            <div className="gas-shop-variante-header">
                {v.icon && <span className="gas-shop-variante-icon">{v.icon}</span>}
                <span className="gas-shop-variante-name">{v.nom}</span>
                <VarianteStock v={v} t={t} />
                {v.in_cart && (
                    <span className="gas-shop-in-cart-badge">{t('shop.product.in_cart_qty', { count: v.cart_quantity })}</span>
                )}
            </div>

            {v.description && <div className="gas-shop-variante-description">{v.description}</div>}

            <VariantePurchaseBadge v={v} t={t} />

            <div className="gas-shop-variante-price-section">
                {hasRestriction && (
                    <div className="gas-shop-variante-restrictions">
                        {v.restrictions.indexOf('membres') !== -1 && (
                            <span className="gas-shop-restriction-badge gas-shop-badge-member">🔒 {t('shop.product.reserved_members')}</span>
                        )}
                        {v.restrictions.indexOf('pass') !== -1 && (
                            <span className="gas-shop-restriction-badge gas-shop-badge-membership-plan">🎓 {t('shop.product.option_unavailable', {}, 'Option indisponible')}</span>
                        )}
                    </div>
                )}

                {blocked ? (
                    <div className="gas-shop-access-restricted">
                        {v.member_blocked && (
                            <p className="gas-shop-restricted-message">
                                🔒{' '}
                                {!cfg.is_logged_in ? (
                                    <>
                                        <a href={cfg.login_url}>{t('shop.product.login_link')}</a>{' '}
                                        {t('common.or')}{' '}
                                        <a href={cfg.adhesion_url}>{t('shop.product.become_member_link')}</a>
                                    </>
                                ) : (
                                    <>
                                        {t('shop.product.member_reserved_sentence')}{' '}
                                        <a href={cfg.adhesion_url}>{t('shop.product.become_member_link')}</a>
                                    </>
                                )}
                            </p>
                        )}
                        {v.pass_blocked && (
                            <p className="gas-shop-restricted-message">
                                🎓 {t('shop.product.option_unavailable_sentence', {}, "Cette option n'est plus disponible.")}
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {v.validation.warnings && v.validation.warnings.length > 0 && (
                            <div className="gas-shop-validation-warnings">
                                {v.validation.warnings.map((w, wi) => (
                                    <div key={wi} className="gas-shop-warning-message">
                                        <span className="gas-warning-icon">⚠️</span>
                                        <span className="gas-warning-text">{w}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {v.in_cart && !v.purchase_info.already_purchased ? (
                            <div className="gas-shop-variante-in-cart">
                                <p className="gas-shop-in-cart-message">{t('shop.product.option_in_cart')}</p>
                                <a href={cfg.cart_url} className="gas-shop-action-button gas-shop-btn-cart">{t('shop.cart.view_cart')}</a>
                            </div>
                        ) : !v.validation.valid ? (
                            <div className="gas-shop-cannot-purchase">
                                <div className="gas-shop-validation-errors">
                                    {v.validation.errors.map((err, ei) => (
                                        <p key={ei} className="gas-shop-error-message"><strong>✗</strong> {err}</p>
                                    ))}
                                </div>
                                <button type="button" className="gas-shop-form-add-to-cart-button gas-shop-button-disabled" disabled>
                                    {t('shop.product.not_available')}
                                </button>
                            </div>
                        ) : v.show_form && v.form ? (
                            <>
                                {isNameYourPrice ? (
                                    <NameYourPriceChooser
                                        cfg={cfg}
                                        amount={nameYourPriceAmount}
                                        onAmountChange={onNameYourPriceAmountChange}
                                        varianteId={v.variante_id}
                                        t={t}
                                    />
                                ) : (
                                <div className="gas-shop-price-display-section">
                                    <div className="gas-shop-price-display">
                                        {v.price.is_invitation && v.price.original_price && v.price.original_price > 0 ? (
                                            <>
                                                <span className="gas-shop-price-free gas-shop-invitation-price">{t('shop.price.free').toUpperCase()}</span>
                                                <span className="gas-shop-price-original"><del>{formatPrice(v.price.original_price)}</del></span>
                                                <p className="gas-shop-invitation-savings">{t('shop.invitation.offered')}</p>
                                            </>
                                        ) : v.price.is_free ? (
                                            <>
                                                <span className="gas-shop-price-free">{t('shop.price.free')}</span>
                                                <div className="gas-shop-price-badges">
                                                    {v.price.show_member_badge && cfg.is_member && (
                                                        <small className="gas-shop-form-member-badge">{t('shop.product.member_rate', { association: cfg.association_name })}</small>
                                                    )}
                                                </div>
                                                {v.price.original_price && v.price.original_price > 0 && (
                                                    <span className="gas-shop-price-original"><del>{formatPrice(v.price.original_price)}</del></span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <span className="gas-shop-price-current">{formatPrice(v.price.current_price)}</span>
                                                {v.price.show_member_badge && cfg.is_member && (
                                                    <div className="gas-shop-price-badges">
                                                        <small className="gas-shop-form-member-badge">{t('shop.product.member_rate', { association: cfg.association_name })}</small>
                                                    </div>
                                                )}
                                                {v.price.original_price && v.price.original_price > v.price.current_price && (
                                                    <>
                                                        <span className="gas-shop-price-original"><del>{formatPrice(v.price.original_price)}</del></span>
                                                        {v.price.show_savings && cfg.is_member && (
                                                            <small className="gas-shop-savings">{t('shop.product.you_save', { amount: formatPrice(v.price.original_price - v.price.current_price) })}</small>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                )}

                                {!isNameYourPrice && v.pricing_notice && v.pricing_notice.message && (
                                    <div className="gas-shop-pricing-notice-compact">
                                        <span className="gas-pricing-icon">💰</span>
                                        <span className="gas-pricing-text" dangerouslySetInnerHTML={{ __html: v.pricing_notice.message }} />
                                    </div>
                                )}

                                <form className="gas-shop-add-to-cart-form" onSubmit={(e) => { e.preventDefault(); onAdd(); }}>
                                    <div className="gas-shop-form-actions">
                                        {v.form.show_quantity_selector && (
                                            <div className="gas-shop-quantity-selector">
                                                <label htmlFor={`quantity_${v.variante_id}`}>{t('shop.cart.quantity_label')}</label>
                                                <select
                                                    id={`quantity_${v.variante_id}`}
                                                    value={quantity}
                                                    onChange={(e) => onQuantityChange(Number(e.target.value))}
                                                >
                                                    {Array.from({ length: Math.min(v.form.max_effective, 20) }, (_, i) => i + 1).map((n) => (
                                                        <option key={n} value={n}>{n}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <button type="submit" className="gas-shop-form-add-to-cart-button" disabled={adding}>
                                            {adding ? t('shop.product.adding') : v.form.button_text}
                                        </button>
                                    </div>

                                    {cfg.show_gift_section && (
                                        <div className="gas-shop-gift-section">
                                            <label className="gas-shop-gift-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={gift.isGift}
                                                    onChange={(e) => onGiftChange({ ...gift, isGift: e.target.checked })}
                                                />
                                                <span>{t('shop.product.gift_toggle')}</span>
                                            </label>
                                            {gift.isGift && (
                                                <div className="gas-shop-gift-fields">
                                                    <label htmlFor={`gift_email_${v.variante_id}`}>{t('shop.product.gift_email_label')}</label>
                                                    <input
                                                        type="email"
                                                        id={`gift_email_${v.variante_id}`}
                                                        value={gift.email}
                                                        required={gift.isGift}
                                                        onChange={(e) => onGiftChange({ ...gift, email: e.target.value })}
                                                    />
                                                    <label htmlFor={`gift_message_${v.variante_id}`}>{t('shop.product.gift_message_label')}</label>
                                                    <textarea
                                                        id={`gift_message_${v.variante_id}`}
                                                        rows={2}
                                                        value={gift.message}
                                                        onChange={(e) => onGiftChange({ ...gift, message: e.target.value })}
                                                    />
                                                    <small className="gas-shop-gift-help">{t('shop.product.gift_help')}</small>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="gas-shop-quantity-limits">
                                        <VarianteQuantityLimits v={v} t={t} />
                                    </div>
                                </form>
                            </>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}
